import type { ReactNode } from 'react'

type Item = {
  id: string
  label: string
  icon: ReactNode
  hero?: boolean
  onClick?: () => void
  /** Accessible name — required for icon-only items (e.g. the hero button). */
  ariaLabel?: string
  /** Optional notification count shown as a badge on the icon. */
  badge?: number
}

const CountBadge = ({ count }: { count: number }) => (
  <span
    aria-hidden
    style={{
      position: 'absolute',
      top: -5,
      right: -9,
      minWidth: 15,
      height: 15,
      padding: '0 3px',
      boxSizing: 'border-box',
      borderRadius: 8,
      border: '1.5px solid var(--paper-2)',
      background: 'var(--terra)',
      color: '#FFF6E8',
      fontFamily: 'var(--font-display)',
      fontSize: 9,
      fontWeight: 700,
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {count > 9 ? '9+' : count}
  </span>
)

export type BottomNavProps = {
  active?: string
  items: Item[]
}

export const BottomNav = ({ active, items }: BottomNavProps) => (
  <div
    style={{
      position: 'sticky',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'var(--paper-2)',
      borderTop: '1px solid var(--line)',
      padding: '6px 12px 12px',
      display: 'flex',
      alignItems: 'center',
    }}
  >
    {items.map((it) =>
      it.hero ? (
        <button
          key={it.id}
          type="button"
          onClick={it.onClick}
          aria-label={it.ariaLabel}
          style={{
            width: 50,
            height: 50,
            borderRadius: 25,
            background: 'var(--terra)',
            color: '#FFF6E8',
            border: 'none',
            // Centre the icon in the disc — without this the block-level
            // SVG sits at the top-left of the button box.
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: -22,
            cursor: 'pointer',
            boxShadow:
              '0 1px 0 rgba(255,255,255,.25) inset, 0 -2px 0 rgba(0,0,0,.18) inset, 0 4px 12px rgba(180,80,50,.4)',
          }}
        >
          {it.icon}
        </button>
      ) : (
        <button
          key={it.id}
          type="button"
          onClick={it.onClick}
          style={{
            // Equal-width tab cells. With four flex:1 tabs flanking the
            // fixed-width hero, the hero lands exactly at the bar's
            // centre regardless of differing label widths.
            flex: 1,
            minWidth: 0,
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            padding: '4px 8px',
            cursor: 'pointer',
            color: active === it.id ? 'var(--ink)' : 'var(--ink-3)',
          }}
        >
          <span style={{ position: 'relative', fontSize: 20, lineHeight: 1 }}>
            {it.icon}
            {it.badge ? <CountBadge count={it.badge} /> : null}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 10,
              fontWeight: 500,
            }}
          >
            {it.label}
          </span>
        </button>
      ),
    )}
  </div>
)
